import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Footer.css";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const sectionMap = useRef({
    home: ".hero, .top-header, main", // Multiple selectors for home
    about: ".about",
    properties: ".properties",
    agents: ".agents-section",
    blog: ".blog",
    contact: ".contact",
  });

  // Function to find section by class name
  const findSectionByClass = (className) => {
    // Try multiple selectors
    const selectors = className.split(",").map((s) => s.trim());

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Found section with selector: ${selector}`);
        return element;
      }
    }

    // If not found with direct selector, try to find by text content
    console.log(`No element found with selectors: ${selectors.join(", ")}`);
    return null;
  };

  // Function to perform the actual scrolling
  const performScroll = (element, sectionKey) => {
    // Added sectionKey parameter
    const header = document.querySelector(".header");
    const headerHeight = header ? header.offsetHeight : 80;

    // Calculate position
    const elementTop = element.offsetTop;
    const scrollPosition = elementTop - headerHeight;

    console.log(
      `Scrolling to position: ${scrollPosition}, element top: ${elementTop}, header height: ${headerHeight}`
    );

    // Smooth scroll
    window.scrollTo({
      top: Math.max(0, scrollPosition), // Ensure not negative
      behavior: "smooth",
    });

    // Update URL without reloading
    window.history.pushState(null, null, `#${sectionKey}`);

    return true;
  };

  // Function to scroll to a section with smooth animation
  const scrollToSection = (sectionKey) => {
    const selectors = sectionMap.current[sectionKey];
    if (!selectors) {
      console.log(`No selectors defined for section: ${sectionKey}`);
      return false;
    }

    const element = findSectionByClass(selectors);
    if (!element) {
      console.log(
        `Could not find element for section: ${sectionKey} with selectors: ${selectors}`
      );

      // Try to find by section title text as fallback
      const allSections = document.querySelectorAll("section");
      for (const section of allSections) {
        const heading = section.querySelector("h1, h2, h3, .section-title");
        if (
          heading &&
          heading.textContent.toLowerCase().includes(sectionKey.toLowerCase())
        ) {
          console.log(`Found section by heading text: ${heading.textContent}`);
          return performScroll(section, sectionKey);
        }
      }

      return false;
    }

    return performScroll(element, sectionKey);
  };
  const handleNavClick = (e, sectionKey) => {
    e.preventDefault();
    setIsMenuOpen(false);

    console.log(`Clicked ${sectionKey}, current page: ${location.pathname}`);

    // If we're on home page, try to scroll
    if (location.pathname === "/") {
      // Small delay to ensure any state updates are processed
      setTimeout(() => {
        // First, try to find and scroll
        let scrolled = scrollToSection(sectionKey);

        if (!scrolled) {
          console.log(
            `Section ${sectionKey} not found immediately, trying again...`
          );

          // Try again with different delays
          const retryDelays = [100, 200, 500];
          let retryCount = 0;

          const retryScroll = () => {
            if (retryCount < retryDelays.length) {
              setTimeout(() => {
                console.log(`Retry ${retryCount + 1} for ${sectionKey}`);
                scrolled = scrollToSection(sectionKey);
                if (!scrolled && retryCount < retryDelays.length - 1) {
                  retryCount++;
                  retryScroll();
                }
              }, retryDelays[retryCount]);
              retryCount++;
            }
          };

          retryScroll();
        }
      }, 50);
    } else {
      // Navigate to home page with hash
      console.log(`Navigating to home page with section: ${sectionKey}`);
      navigate(`/#${sectionKey}`);
    }
  };
  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Column 1 */}
        <div className="footer-col">
          <h2 className="brand">ATLAS SPACES</h2>
          <p className="subtext">REAL ESTATE AGENCY</p>
          <p>
            A small river named Duden flows by their place and supplies it with
            the necessary regelialia.
          </p>

          <div className="social-icons">
            <span>
              <i className="fab fa-twitter"></i>
            </span>
            <span>
              <i className="fab fa-facebook-f"></i>
            </span>
            <span>
              <i className="fab fa-instagram"></i>
            </span>
          </div>
        </div>

        {/* Column 2 */}
        <div className="footer-col">
          <h3>Offers</h3>
          <ul>
            <li>
              <i
                href="#"
                style={{
                  color: "#ffffff", // Changed from #ffffffff to #ffffff
                  fontSize: "16px",
                  cursor: "pointer",
                  marginLeft: "8px",
                  transition: "all 0.3s ease",
                  font: "unset",
                  fontFamily:
                    "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", // Fixed quotes
                }}
                onClick={(e) => handleNavClick(e, "properties")}
                title="Properties"
                aria-label="Properties"
              >
                Properties
              </i>
            </li>
            <li>
              <i
                href="#"
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  cursor: "pointer",
                  marginLeft: "8px",
                  transition: "all 0.3s ease",
                  font: "unset",
                  fontFamily:
                    "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", // Fixed quotes
                }}
                onClick={(e) => handleNavClick(e, "agents")}
                title="Agents"
                aria-label="Agents"
              >
                Agents
              </i>
            </li>
            <li>
              <i className="fas fa-chevron-right"></i> Locations
            </li>
            <li>
              <i className="fas fa-chevron-right"></i> Clients Support
            </li>
          </ul>
        </div>

        {/* Column 3 */}
        <div className="footer-col">
          <h3>Company</h3>
          <ul>
            <li>
              <a
                href="#"
                className="nav-link"
                onClick={(e) => handleNavClick(e, "home")}
              >
                Home
              </a>
            </li>
            <li>
              <a
                href="#"
                className="nav-link"
                onClick={(e) => handleNavClick(e, "about")}
              >
                About
              </a>
            </li>
            <li>
              <a
                href="#"
                className="nav-link"
                onClick={(e) => handleNavClick(e, "blog")}
              >
                Blog
              </a>
            </li>
            <li>
              <a
                href="#"
                className="nav-link"
                onClick={(e) => handleNavClick(e, "contact")}
              >
                Contact Us
              </a>
            </li>
          </ul>
        </div>

        {/* Column 4 */}
        <div className="footer-col">
          <h3>Have a Questions?</h3>

          <div className="contact-row">
            <i className="fas fa-map-marker-alt"></i>
            <p>Sinhagad Road, Near Plaza, Pune - 411011, Pune, Maharashtra</p>
          </div>

          <div className="contact-row">
            <i className="fas fa-phone"></i>
            <p>+91 9067 161 025</p>
          </div>

          <div className="contact-row">
            <i className="fas fa-paper-plane"></i>
            <p>info@atlasspaces.in</p>
          </div>
        </div>
      </div>

      <div className="footer-bottom">Copyright ©2025 All rights reserved</div>
    </footer>
  );
};

export default Footer;
